import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, useTheme } from '@mui/material';
import { Capacitor } from '@capacitor/core';
import ilmoLogoBlack from '../assets/graphics-black/ilmo_logo.svg';
import ilmoLogoWhite from '../assets/graphics-white/ilmo_logo.svg';
import dayjs from 'dayjs';

interface AppStatusDialogProps {
  open: boolean;
  title: string;
  message: string;
  buttonText?: string;
  buttonLink?: string;
  dismissable: boolean;
  isBlocking: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

export default function AppStatusDialog({
  open,
  title,
  message,
  buttonText,
  buttonLink,
  dismissable,
  isBlocking,
  onClose,
  onUpdate
}: AppStatusDialogProps) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const logoSrc = isDarkMode ? ilmoLogoWhite : ilmoLogoBlack;

  const handleUpdateApp = () => {
    if (buttonLink) {
      window.open(buttonLink, '_blank');
    }
    onUpdate();
  };

  return (
    <Dialog
      open={open}
      onClose={dismissable ? onClose : undefined}
      disableEscapeKeyDown={!dismissable}
      fullWidth
      maxWidth="sm"
      slotProps={isBlocking ? {
        backdrop: {
          style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' }
        }
      } : undefined}
      PaperProps={{
        sx: {
          overflow: 'hidden'
        }
      }}
    >
      {/* Logo Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 3,
          px: 3,
          background: isBlocking
            ? `linear-gradient(135deg, ${theme.palette.primary.main}10, ${theme.palette.secondary.main}20)`
            : `linear-gradient(135deg, ${theme.palette.primary.main}10, ${theme.palette.secondary.main}20)`,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <img
          src={logoSrc}
          alt="Ilmo"
          style={{
            height: '32px',
            width: 'auto',
            filter: isBlocking ? 'drop-shadow(0 2px 8px rgba(0,0,0,0.1))' : 'none'
          }}
        />
      </Box>

      <DialogTitle sx={{ pb: 1, pt: 3, textAlign: 'center' }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'center', px: 4, pt: "0px !important" }}>
        <Typography variant="body1" sx={{
          fontSize: '1rem',
          color: theme.palette.text.secondary,
          whiteSpace: 'pre-line',
        }}>
          {message}
        </Typography>
      </DialogContent>

      <DialogActions sx={{
        px: 4,
        pb: 4,
        pt: 3,
        flexDirection: 'column',
        gap: 1.5,
        alignItems: 'stretch'
      }}>
        {buttonText && buttonLink && (
          <Button
            variant="contained"
            onClick={handleUpdateApp}
            size="large"
            sx={{
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              border: 'none',
            }}
          >
            {buttonText}
          </Button>
        )}
        {dismissable && (
          <Button
            variant="text"
            onClick={() => {
              onClose();
              localStorage.setItem('ilmo_lastTimeMinorMsgDismissedTime', dayjs().toISOString());
            }}
            size="large"
            sx={{
              fontSize: '0.95rem',
              fontWeight: 500,
              textTransform: 'none',
              color: theme.palette.text.secondary,
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                color: theme.palette.text.primary
              },
              '&:active': {
                backgroundColor: theme.palette.action.selected
              },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {isBlocking ? "Peruuta" : "Myöhemmin"}
          </Button>
        )}
      </DialogActions>
    </Dialog >
  );
}
